// auditpm — Impartiality Register (organisation-level, standalone).
// Single editable table. No scoring, no formulas, no charts, no linking to individual audits.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../lib/AuthContext'
import { toast, failed } from '../lib/toast'

const LMH = ['Low', 'Medium', 'High']
const STATUSES = ['Open', 'Closed']

const BLANK = {
  risk_id: '', risk_description: '', risk_category: '', threat_source: '', affected_area: '',
  related_client: '', related_person: '', related_job_ref: '',
  likelihood: '', impact: '', risk_level: '', mitigation: '', residual_risk: '',
  owner: '', status: 'Open', outcome: '', reviewed_by: '', last_reviewed_date: '',
  review_frequency: 'Annual', revision_no: 0, revised_by: '',
}

// Auto Risk ID: next R{n} from existing rows (user never enters/edits this).
function computeNextRiskId(rows) {
  let max = 0
  for (const r of (rows || [])) {
    const m = /^R(\d+)$/.exec(r.risk_id || '')
    if (m) max = Math.max(max, parseInt(m[1], 10))
  }
  return `R${max + 1}`
}

// Flat, read-only full-register view for printing / showing an assessor (no click-into-rows).
function printRegister(rows) {
  const esc = s => (s == null ? '' : String(s)).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const fmtd = d => { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch { return '—' } }
  const body = (rows || []).map(r => `<tr>
    <td>${esc(r.risk_id)}</td><td>${esc(r.risk_description)}</td><td>${esc(r.risk_category)}</td>
    <td>${esc(r.affected_area)}</td><td>${esc(r.likelihood)}</td><td>${esc(r.impact)}</td><td>${esc(r.risk_level)}</td>
    <td>${esc(r.owner)}</td><td>${esc(r.mitigation)}</td><td>${esc(r.residual_risk)}</td><td>${esc(r.outcome)}</td>
    <td>${esc(r.status)}</td><td>${esc(r.revised_by || r.reviewed_by)}</td><td>${fmtd(r.last_reviewed_date)}</td></tr>`).join('')
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Impartiality Register</title>
    <style>@page{size:A4 landscape;margin:12mm}body{font-family:Arial,Helvetica,sans-serif;color:#1a2e2e;margin:0;font-size:10.5px}
    h1{font-size:16px;margin:0 0 2px;color:#003333}.sub{color:#666;font-size:11px;margin-bottom:10px}
    table{width:100%;border-collapse:collapse}th,td{border:0.5px solid #bbb;padding:4px 6px;text-align:left;vertical-align:top}
    th{background:#e0f2f2;color:#003333;font-weight:600;font-size:10px}</style></head><body>
    <h1>Impartiality Register</h1><div class="sub">CCIS Singapore Pte Ltd · generated ${new Date().toLocaleDateString('en-SG')}</div>
    <table><thead><tr><th>ID</th><th>Description</th><th>Category</th><th>Affected area</th><th>L</th><th>I</th><th>Level</th>
    <th>Owner</th><th>Mitigation</th><th>Residual risk</th><th>Outcome</th><th>Status</th><th>Reviewed by</th><th>Last reviewed</th></tr></thead>
    <tbody>${body || '<tr><td colspan="14">No entries.</td></tr>'}</tbody></table></body></html>`
  const w = window.open('', '_blank'); if (!w) { toast('Allow pop-ups to view or print', 'warning'); return }
  w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300)
}

export default function ImpartialityRegister() {
  const { profile, logAction, canApprove } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)  // null = closed, object = form (no id = new)
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('impartiality_register')
      .select('*').order('created_at', { ascending: true })
    if (error) console.warn('impartiality_register load error (run PHASE1_SAC.sql):', error.message)
    setRows(data || [])
    setLoading(false)
  }

  function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }

  async function save() {
    if (!editing) return
    const f = editing
    if (!(f.risk_description || '').trim()) { toast('Enter the risk description', 'warning'); return }
    setSaving(true)
    const payload = {
      // Risk ID is auto-assigned on create and never edited afterwards.
      risk_id: f.id ? (f.risk_id || null) : computeNextRiskId(rows),
      risk_description: f.risk_description || null,
      risk_category: f.risk_category || null,
      threat_source: f.threat_source || null,
      affected_area: f.affected_area || null,
      related_client: f.related_client || null,
      related_person: f.related_person || null,
      related_job_ref: f.related_job_ref || null,
      likelihood: f.likelihood || null,
      impact: f.impact || null,
      risk_level: f.risk_level || null,
      mitigation: f.mitigation || null,
      residual_risk: f.residual_risk || null,
      owner: f.owner || null,
      status: f.status || 'Open',
      outcome: f.outcome || null,
      reviewed_by: f.reviewed_by || null,
      last_reviewed_date: f.last_reviewed_date || null,
      review_frequency: f.review_frequency || null,
      updated_at: new Date().toISOString(),
    }
    let error
    if (f.id) {
      ({ error } = await supabase.from('impartiality_register').update(payload).eq('id', f.id))
    } else {
      ({ error } = await supabase.from('impartiality_register').insert({ ...payload, revision_no: 0 }))
    }
    setSaving(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction(f.id ? 'Impartiality risk updated' : 'Impartiality risk added',
      'impartiality_register', f.id || null, f.risk_id || f.risk_description?.slice(0, 40), {})
    setEditing(null)
    load()
  }

  async function markReviewed(row) {
    const today = new Date().toISOString().split('T')[0]
    const updates = {
      last_reviewed_date: today,
      revised_by: profile?.name || 'Staff',
      revision_no: (row.revision_no || 0) + 1,
      updated_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('impartiality_register').update(updates).eq('id', row.id)
    if (error) { failed("Couldn't mark it reviewed", error.message); return }
    if (logAction) logAction('Impartiality risk reviewed', 'impartiality_register', row.id, row.risk_id || row.risk_description?.slice(0, 40), {})
    load()
  }

  async function remove(row) {
    if (!window.confirm('Delete this risk entry?')) return
    const { error } = await supabase.from('impartiality_register').delete().eq('id', row.id)
    if (error) { failed("Couldn't delete", error.message); return }
    load()
  }

  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }))

  return (
    <div>
      <div className="page-header">
        <h1>Impartiality Register</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => printRegister(rows)}>View / print register</button>
          {canApprove
            ? <button className="primary" onClick={() => setEditing({ ...BLANK })}>+ Add risk</button>
            : <span style={{ fontSize: 13, color: 'var(--text3)' }}>View only — contact the Quality Manager to make changes.</span>}
        </div>
      </div>

      <div className="notice notice-info" style={{ fontSize: 13, marginBottom: 16 }}>
        Organisation-level register of impartiality and conflict-of-interest risks. Review periodically and use
        “Mark as reviewed” to stamp the review date and bump the revision number.
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Risk ID</th><th>Description</th><th>Category</th><th>Affected area</th>
                <th>Likelihood</th><th>Impact</th><th>Risk level</th><th>Owner</th>
                <th>Status</th><th>Last reviewed</th><th>Rev</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={12} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No risks recorded yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setEditing({
                  ...r,
                  last_reviewed_date: r.last_reviewed_date || '',
                })}>
                  <td style={{ fontWeight: 500 }}>{r.risk_id || '—'}</td>
                  <td style={{ maxWidth: 260 }}>{r.risk_description || '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 13 }}>{r.risk_category || '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 13 }}>{r.affected_area || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.likelihood || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.impact || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.risk_level || '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 13 }}>{r.owner || '—'}</td>
                  <td><span className={`badge ${r.status === 'Closed' ? 'badge-ok' : 'badge-warn'}`}>{r.status || 'Open'}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.last_reviewed_date)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text3)' }}>
                    {r.revision_no || 0}{r.revised_by ? <div style={{ fontSize: 13 }}>{r.revised_by}</div> : null}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {canApprove ? <>
                      <button className="sm" onClick={() => markReviewed(r)} style={{ marginRight: 6 }}>Mark reviewed</button>
                      <button className="sm danger" onClick={() => remove(r)}>Delete</button>
                    </> : <span style={{ fontSize: 13, color: 'var(--text3)' }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => !saving && setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 50 }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, width: '100%' }}>
            <h2 style={{ marginBottom: 14 }}>{editing.id ? 'Edit risk' : 'Add risk'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div className="field"><label>Risk ID</label><input readOnly value={editing.id ? (editing.risk_id || '—') : 'Auto (assigned on save)'} style={{ background: 'var(--bg2)', color: 'var(--text3)' }} /></div>
              <div className="field"><label>Risk Category</label><input value={editing.risk_category || ''} onChange={e => set('risk_category', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Risk Description *</label><textarea rows={2} value={editing.risk_description || ''} onChange={e => set('risk_description', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Threat / conflict source</label><input value={editing.threat_source || ''} onChange={e => set('threat_source', e.target.value)} placeholder="What gives rise to the threat (e.g. prior consultancy, financial, personal relationship)" /></div>
              <div className="field"><label>Related client</label><input value={editing.related_client || ''} onChange={e => set('related_client', e.target.value)} /></div>
              <div className="field"><label>Related person</label><input value={editing.related_person || ''} onChange={e => set('related_person', e.target.value)} placeholder="Auditor / staff, if applicable" /></div>
              <div className="field"><label>Related job / quote ref</label><input value={editing.related_job_ref || ''} onChange={e => set('related_job_ref', e.target.value)} /></div>
              <div className="field"><label>Affected Area</label><input value={editing.affected_area || ''} onChange={e => set('affected_area', e.target.value)} /></div>
              <div className="field"><label>Owner</label><input value={editing.owner || ''} onChange={e => set('owner', e.target.value)} /></div>
              <div className="field"><label>Likelihood</label>
                <select value={editing.likelihood || ''} onChange={e => set('likelihood', e.target.value)}>
                  <option value="">—</option>{LMH.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="field"><label>Impact</label>
                <select value={editing.impact || ''} onChange={e => set('impact', e.target.value)}>
                  <option value="">—</option>{LMH.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="field"><label>Risk Level</label>
                <select value={editing.risk_level || ''} onChange={e => set('risk_level', e.target.value)}>
                  <option value="">—</option>{LMH.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="field"><label>Status</label>
                <select value={editing.status || 'Open'} onChange={e => set('status', e.target.value)}>
                  {STATUSES.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Mitigation / control applied</label><textarea rows={2} value={editing.mitigation || ''} onChange={e => set('mitigation', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Residual Risk</label><textarea rows={2} value={editing.residual_risk || ''} onChange={e => set('residual_risk', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Outcome / decision</label><textarea rows={2} value={editing.outcome || ''} onChange={e => set('outcome', e.target.value)} placeholder="Evaluation outcome, e.g. acceptable with controls / proceed / decline" /></div>
              <div className="field"><label>Reviewed by</label><input value={editing.reviewed_by || ''} onChange={e => set('reviewed_by', e.target.value)} placeholder={profile?.name || ''} /></div>
              <div className="field"><label>Reviewed date</label><input type="date" value={editing.last_reviewed_date || ''} onChange={e => set('last_reviewed_date', e.target.value)} /></div>
              <div className="field"><label>Review Frequency</label><input value={editing.review_frequency || ''} onChange={e => set('review_frequency', e.target.value)} placeholder="e.g. Annual" /></div>
            </div>
            {editing.id && (
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 10 }}>
                Revision {editing.revision_no || 0}{editing.revised_by ? ` · last revised by ${editing.revised_by}` : ''}
                {editing.updated_at ? ` · updated ${new Date(editing.updated_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}` : ''}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {canApprove && <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
              <button onClick={() => setEditing(null)} disabled={saving}>{canApprove ? 'Cancel' : 'Close'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
